import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { trackConversion, whenUserPropertiesReady } from '@/lib/analytics';
import { getFlowIntent } from '@/lib/flowIntent';
import { readEntryMarker } from '@/lib/entryMarker';
import { useRealtimePostgresChanges } from '@/hooks/useRealtimePostgresChanges';

/**
 * Meldet den Meilenstein "erster Merchant-Center-Anschluss" als `feed_connected`.
 *
 * Warum hier und nicht im OAuth-Callback: `GoogleCallback.tsx` sieht nur, dass
 * ein Google-Konto gespeichert wurde. Ob dieses Konto ein Merchant Center
 * traegt, ist dort unbekannt, `merchant_centers` fuellt erst `fetch-account-data`
 * spaeter. Live sind das 11 externe Konten mit Google-Konto gegen 7 mit
 * Merchant Center (gemessen 22.08.2026), der Unterschied ist also real. Dazu
 * kommt: der Weg ueber die Marketing-Seite laeuft gar nicht durch den Callback,
 * weil `google-oauth-handler` die `google_accounts`-Zeile serverseitig anlegt
 * und direkt in die App weiterleitet. Der Hook liegt deshalb im Layout, dem
 * einzigen Rahmen aller geschuetzten Routen, und wird dort GENAU EINMAL
 * montiert.
 *
 * Das Signal ist `count(*) > 0` ueber `merchant_centers` OHNE Status-Filter: die
 * Zeile entsteht nur nach einer erfolgreichen Auflistung durch
 * `fetch-account-data`, und ein spaeter widerrufenes Merchant Center aendert
 * nichts daran, dass der Meilenstein erreicht war.
 *
 * Die Einmaligkeit liegt in der Datenbank (`profiles.conversion_mc_connected_tracked_at`),
 * nicht im Browser: ein Zustand, den jeder Seitenaufbau erneut vorfindet, wuerde
 * sonst bei jedem Login erneut melden, und zwei offene Tabs wuerden doppelt
 * melden. Der Compare-and-Set laesst genau einen Lauf gewinnen.
 *
 * WARUM DER HOOK NICHT NUR BEIM MONTIEREN PRUEFT. Ein Neu-Nutzer aus dem
 * Feed-Check-Trichter betritt die App mit NULL Merchant Centern: der Assistent
 * oeffnet sich erst danach, und `fetch-account-data` legt die Zeilen erst am
 * Ende dieses Vorgangs an. Eine Pruefung allein beim Montieren faende also
 * genau bei dem Nutzer nichts, fuer den diese Bruecke gebaut wurde, und der
 * Meilenstein fiele auf den naechsten Seitenaufbau. Bis dahin ist der
 * Flow-Marker womoeglich vom Report-View verbraucht und der Entry-Marker in
 * einem neuen Tab verloren: die Konversion traege dann `flow: 'none'` und
 * `entry: 'app'` und waere vom In-App-Fall nicht mehr unterscheidbar. Genau
 * diese Unterscheidung ist der Zweck der beiden Parameter.
 *
 * Deshalb horcht der Hook zusaetzlich auf das INSERT in `merchant_centers` und
 * prueft dann erneut. Das Abo laeuft ueber `useRealtimePostgresChanges` (in
 * dieser App die einzige erlaubte Art, einen Realtime-Kanal zu erzeugen, per
 * Invarianten-Test festgehalten) und wird abgeschaltet, sobald der Meilenstein
 * abgeschlossen ist: `topic === null` heisst "gar nicht abonnieren". Ein
 * Bestandskonto zahlt also keinen Kanal, nur den einen Profil-Select beim
 * Montieren. Ein Filter auf den Nutzer ist weder moeglich noch noetig:
 * `merchant_centers` traegt keine `user_id`-Spalte, und die RLS-Policy der
 * Tabelle scoped den Kanal ohnehin auf die eigenen Zeilen.
 *
 * Der Schnappschuss von `flow` und `entry` wird BEIM MONTIEREN genommen und in
 * einem Ref gehalten, damit die spaetere Meldung dieselben Werte traegt wie
 * eine sofortige. Ohne das Ref waere der Umweg ueber Realtime genau das
 * Problem, das er loesen soll.
 *
 * Jeder Fehler wird STILL geschluckt. Analytics darf nie einen Nutzerpfad
 * brechen; gleiche Haltung wie `AuthContext.pushUserPropertiesWithInternalFlag`.
 */
export function useConversionMilestones(): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Ein Ref statt eines State: die Sperre darf kein erneutes Rendern ausloesen.
  // Gespeichert wird die bereits behandelte User-ID, nicht bloss ein Ja/Nein.
  // Beides zusammen: der StrictMode-Doppelmount findet dieselbe ID vor und
  // laeuft nicht zweimal, ein echter Kontowechsel bekommt aber seinen eigenen
  // Lauf.
  const schnappschussRef = useRef<{ userId: string; flow: 'feedcheck' | 'none'; entry: 'web' | 'app' } | null>(null);
  // Laeuft gerade eine Pruefung? Ein zweites INSERT waehrend des Rundlaufs
  // darf keinen zweiten Rundlauf starten; der Compare-and-Set faenge das zwar
  // ab, aber zwei parallele Laeufe koennten beide eine Zeile zurueckbekommen
  // sehen, wenn der eine den anderen ueberholt.
  const laeuftRef = useRef(false);
  // Abgeschlossen heisst: Marke steht (egal ob von uns oder schon vorher).
  // Erst dieser Zustand schaltet das Realtime-Abo ab, deshalb State und nicht
  // Ref: die Abschaltung muss ein Rendern ausloesen.
  const [abgeschlossen, setAbgeschlossen] = useState(false);

  const pruefe = useCallback(async () => {
    const schnappschuss = schnappschussRef.current;
    if (!schnappschuss) return;
    if (laeuftRef.current) return;
    laeuftRef.current = true;
    try {
      // Kurzschluss zuerst: der Normalfall ist ein Bestandskonto, dessen
      // Marke laengst steht. Ein Select gegen die eigene Profilzeile ist
      // billiger als ein Count ueber merchant_centers.
      const { data: profil, error: profilFehler } = await supabase
        .from('profiles')
        .select('conversion_mc_connected_tracked_at')
        .eq('user_id', schnappschuss.userId)
        .maybeSingle();
      if (profilFehler) return;
      // Die Spalte fehlt im generierten Supabase-Typ bis zum naechsten
      // Typegen (gleiche Lage wie `is_internal` in AuthContext.tsx).
      const zeile = profil as unknown as
        | { conversion_mc_connected_tracked_at?: string | null }
        | null;
      if (zeile?.conversion_mc_connected_tracked_at) {
        setAbgeschlossen(true);
        return;
      }

      // Kein Status-Filter, siehe Kopfkommentar. `head: true` holt keine
      // Zeilen, nur die Zahl; Muster aus FeedHealthDashboard.tsx.
      const { count, error: zaehlFehler } = await supabase
        .from('merchant_centers')
        .select('*', { count: 'exact', head: true })
        .limit(1);
      if (zaehlFehler) return;
      // Noch kein Merchant Center: NICHT abschliessen. Der Nutzer steht
      // gerade erst im Assistenten, das Realtime-Abo bleibt an und weckt uns.
      if ((count ?? 0) === 0) return;

      // Compare-and-Set. Nur wer eine Zeile zurueckbekommt, hat den
      // Meilenstein tatsaechlich als Erster gesetzt und darf melden.
      const { data: geschrieben, error: schreibFehler } = await supabase
        .from('profiles')
        // Cast nur fuer die Typpruefung, das gesendete JSON ist unveraendert.
        .update({ conversion_mc_connected_tracked_at: new Date().toISOString() } as never)
        .eq('user_id', schnappschuss.userId)
        .is('conversion_mc_connected_tracked_at', null)
        .select('user_id');
      if (schreibFehler) return;
      // Ab hier steht die Marke, entweder durch uns oder durch einen
      // gleichzeitigen Lauf im zweiten Tab. In beiden Faellen ist der
      // Meilenstein erledigt und das Abo kann weg.
      setAbgeschlossen(true);
      if (!geschrieben || geschrieben.length === 0) return;

      // GA4 wendet eine spaeter gesetzte User-Property nicht rueckwirkend an.
      // Ohne dieses Warten traegt die Konversion kein `user_internal`, und
      // interne Konten sind live 3 von 3 der Testkonten. Der Timeout in
      // analytics.ts sorgt dafuer, dass ein haengender Profil-Select das
      // Ereignis trotzdem nicht verschluckt.
      await whenUserPropertiesReady();
      trackConversion('feed_connected', {
        flow: schnappschuss.flow,
        entry: schnappschuss.entry,
      });
    } catch {
      // Still schlucken, Begruendung im Kopfkommentar.
    } finally {
      laeuftRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    if (schnappschussRef.current?.userId === userId) return;

    // Schnappschuss BEIM MOUNT, nicht beim Melden: `FeedHealthDashboard`
    // verbraucht den Flow-Marker beim ersten Report-View, und der Marker
    // verfaellt zusaetzlich nach 24h. Wer erst nach dem Datenbank-Rundlauf
    // liest, liest womoeglich schon ins Leere.
    schnappschussRef.current = {
      userId,
      flow: getFlowIntent() === 'feedcheck' ? 'feedcheck' : 'none',
      entry: readEntryMarker(),
    };
    // Ein Kontowechsel im selben Tab beginnt bei null: der neue Nutzer hat
    // seinen eigenen Meilenstein.
    setAbgeschlossen(false);
    void pruefe();
  }, [userId, pruefe]);

  useRealtimePostgresChanges(
    userId && !abgeschlossen ? `conversion-milestones:${userId}` : null,
    { event: 'INSERT', schema: 'public', table: 'merchant_centers' },
    () => {
      void pruefe();
    },
  );
}
